import { format, isSameDay } from 'date-fns';
import type { AgentTaskBucket, RetryFailureCause } from '../types';

export interface RetryNoticeCopy {
  title: string;
  body: string;
}

/**
 * What queued work says while its last attempt failed and the agent waits to try again. The cause
 * comes from the engine (agent-suite retry.ts `failureCause`), never the error text: the owner reads
 * whether the agent's own work was sent back, the AI service was down, or something else broke --
 * and when the next attempt is, as a clock time rather than "shortly".
 */
export function retryNoticeCopy(input: {
  agentName: string;
  bucket: AgentTaskBucket;
  retry: { nextAttemptAt: number; cause?: RetryFailureCause };
  now: number;
}): RetryNoticeCopy {
  const { agentName, bucket, retry, now } = input;
  const next = nextAttemptSentence(agentName, retry.nextAttemptAt, now);
  switch (retry.cause) {
    case 'refused':
      return {
        title: `${agentName}'s ${bucket === 'audit' ? 'report' : 'draft'} didn't pass its final check`,
        body: `Nothing was changed. ${next}`,
      };
    case 'provider':
      return {
        title: `${agentName} couldn't reach the AI service`,
        body: `Nothing was changed. ${next}`,
      };
    default:
      return {
        title: `${agentName} couldn't work on this yet`,
        body: `Something went wrong on our side, so nothing was changed. ${next}`,
      };
  }
}

function nextAttemptSentence(agentName: string, nextAttemptAt: number, now: number): string {
  if (!Number.isFinite(nextAttemptAt) || nextAttemptAt <= now) {
    return `${agentName} tries again within the next few minutes.`;
  }
  const at = new Date(nextAttemptAt);
  const clock = format(at, 'h:mm a');
  return isSameDay(at, new Date(now))
    ? `Next attempt at ${clock}.`
    : `Next attempt on ${format(at, 'MMM d')} at ${clock}.`;
}
